import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useClaimDevice, useOrganisations } from "../../../../hooks/queries";
import { UtilityService } from "../../../../services/UtilityService";
import { useSelector } from "react-redux";

/**
 * Hook for managing device claiming functionality
 * @param {Function} onClaimSuccess - Callback when device is successfully claimed
 * @returns {Object} Device claiming state and handlers
 */
export const useDeviceClaim = (onClaimSuccess) => {
  const { t } = useTranslation("common");

  // TanStack Query data and mutations
  const { data: organisations = [] } = useOrganisations();
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const claimDeviceMutation = useClaimDevice();

  // Form state - initialize with current organisation
  const [claimCode, setClaimCode] = useState("");
  const [organisationID, setOrganisationID] = useState(
    () => selectedOrganisation?.id || organisations?.[0]?.id
  );

  // Reset form state
  const resetForm = () => {
    setClaimCode("");
    setOrganisationID(selectedOrganisation?.id || organisations?.[0]?.id);
  };

  // Handle device claiming
  const claimDevice = async () => {
    claimDeviceMutation.mutate(
      {
        claimCode: claimCode,
        organisationId: organisationID,
      },
      {
        onSuccess: () => {
          UtilityService.updateSnackbarMessage(
            t("common.device_claimed_successfully")
          );

          if (onClaimSuccess) {
            onClaimSuccess();
          }
        },
        onError: (error) => {
          // Handle specific error cases
          let errorMessage;
          if (error.response?.status === 404) {
            errorMessage = t(
              "common.invalid_claim_code",
              "Invalid claim code or device not found"
            );
          } else if (error.response?.status === 409) {
            errorMessage = t(
              "common.device_already_claimed",
              "Device has already been claimed"
            );
          } else {
            errorMessage = t("common.error_claiming_device", {
              error: error.response?.data?.detail || error.message,
            });
          }

          UtilityService.updateSnackbarMessage(errorMessage, true);
        },
      }
    );
  };
  return {
    // Form state
    claimCode,
    setClaimCode,
    organisationID,
    setOrganisationID,
    organisations,

    // Actions
    claimDevice,
    resetForm,

    // Loading state
    isClaimingDevice: claimDeviceMutation.isPending,
  };
};
